import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ErrorCode, Issue } from '@il-colpo/protocol';
import { EmoteGlyph } from '../../game/emotes/EmoteGlyph.tsx';
import { EmoteBar } from '../../game/emotes/Emotes.tsx';
import { useLooseT } from '../../game/text/loose.ts';
import { getConnection, useConnection } from '../../online/onlineConnection.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { Button, Panel, TextField } from '../../ui/index.ts';
import { PlayerActions } from '../social/PlayerActions.tsx';
import styles from './Online.module.css';

/** The sentence for something that went wrong, in the language of the app. */
export function useIssueText(): (issue: Issue, detail?: string) => string {
  const { t } = useTranslation('online');
  const g = useLooseT('game');
  return (issue, detail) => {
    if (issue === 'queueBanned') {
      const seconds = Number(detail);
      return t('errors.queueBanned', {
        minutes: Number.isFinite(seconds) && seconds > 0 ? Math.max(1, Math.ceil(seconds / 60)) : 1,
      });
    }
    // A ban says how long it lasts in seconds, or `forever`; a mute only says it is on.
    if (issue === 'banned') {
      const seconds = Number(detail);
      if (!Number.isFinite(seconds) || seconds <= 0) return t('errors.banned');
      if (seconds < 3600)
        return t('errors.bannedMinutes', { count: Math.max(1, Math.ceil(seconds / 60)) });
      if (seconds < 86_400) return t('errors.bannedHours', { count: Math.ceil(seconds / 3600) });
      return t('errors.bannedDays', { count: Math.ceil(seconds / 86_400) });
    }
    // A refused pick of the role draft has words of its own.
    if (issue === 'illegal' && (detail === 'notYourTurn' || detail === 'taken')) {
      return t(`errors.${detail}`);
    }
    // A refused action carries the engine's own reason, which the game already has words for.
    if (issue === 'illegal' && detail) {
      const key = `errors.${detail}`;
      const text = g(key);
      if (text !== key) return text;
    }
    return t(`errors.${issue}`);
  };
}

/** Shows each new thing that goes wrong as a toast (a refused action, a rate limit, a lost seat). */
export function useIssueToasts(): void {
  const state = useConnection();
  const text = useIssueText();
  const push = useToastStore((s) => s.push);
  const shown = useRef<number | null>(null);
  const issue = state.issue;
  useEffect(() => {
    if (!issue || shown.current === issue.at) return;
    shown.current = issue.at;
    push(text(issue.issue, issue.detail), 'danger');
  });
}

export function IssueLine({
  issue,
  detail,
}: {
  issue: ErrorCode | Issue | null;
  /** More about it: for a queue ban, how many seconds are left. */
  detail?: string | null;
}) {
  const text = useIssueText();
  if (!issue) return null;
  return (
    <p className={styles['error']} role="alert">
      {text(issue, detail ?? undefined)}
    </p>
  );
}

/** The others at the table, each with what can be done about them (in a heist, the window of the chat). */
function TablePeople() {
  const { t } = useTranslation('online');
  const state = useConnection();
  const others = (state.lobby?.players ?? []).filter((p) => p.id !== state.you);
  if (others.length === 0) return null;
  return (
    <div className={styles['stack']}>
      <h3 className={styles['subtitle']}>{t('room.players')}</h3>
      <ul className={styles['players']}>
        {others.map((p) => (
          <li key={p.id} className={styles['player']}>
            <span className={styles['playerName']}>
              <strong>{p.name}</strong>
            </span>
            <PlayerActions handle={p.name} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * What the table says to itself: the log, and a line to add to it (when the player's age allows).
 * With a `title` it is a panel of the room screen; without, it is the content of a window.
 */
export function ChatPanel({ title }: { title?: boolean }) {
  const { t } = useTranslation('online');
  const state = useConnection();
  const [text, setText] = useState('');
  const log = useRef<HTMLDivElement>(null);
  const me = state.lobby?.players.find((p) => p.id === state.you);
  const allowed = me?.freeChat ?? false;
  // What was said and what was shown, in the order it came.
  const lines = [
    ...state.chat.map((c) => ({ ...c, emote: null })),
    ...state.emotes.map((e) => ({ from: e.from, name: e.name, at: e.at, text: '', emote: e.id })),
  ].sort((a, b) => a.at - b.at);

  useEffect(() => {
    log.current?.scrollTo({ top: log.current.scrollHeight });
  }, [lines.length]);

  const send = (e: FormEvent) => {
    e.preventDefault();
    const line = text.trim();
    if (!line || !allowed) return;
    getConnection().say(line);
    setText('');
  };

  const content = (
    <>
      <div className={styles['stack']}>
        <div
          ref={log}
          className={styles['chatLog']}
          role="log"
          aria-live="polite"
          aria-label={t('chat.title')}
        >
          <ol className={styles['chatList']}>
            {lines.length === 0 ? (
              <li className={styles['note']}>{t('chat.empty')}</li>
            ) : (
              lines.map((c, i) => (
                <li key={`${String(c.at)}-${String(i)}`} className={styles['chatLine']}>
                  <b>{c.from === state.you ? t('chat.you') : c.name}</b>
                  {c.emote ? (
                    <span className={styles['chatEmote']}>
                      <EmoteGlyph id={c.emote} size="1.4rem" />
                      <span className="visually-hidden">{t(`chat.emote.${c.emote}`)}</span>
                    </span>
                  ) : (
                    c.text
                  )}
                </li>
              ))
            )}
          </ol>
        </div>
        <EmoteBar />
        {allowed ? (
          <form className={styles['chatForm']} onSubmit={send}>
            <TextField
              label={t('chat.placeholder')}
              value={text}
              maxLength={200}
              autoComplete="off"
              onChange={setText}
            />
            <Button type="submit" disabled={text.trim().length === 0}>
              {t('chat.send')}
            </Button>
          </form>
        ) : (
          <p className={styles['note']}>{t('chat.forbidden')}</p>
        )}
        {title ? null : <TablePeople />}
      </div>
    </>
  );
  return title ? <Panel title={t('chat.title')}>{content}</Panel> : content;
}
